import type { ComponentType, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { statCardSurface } from "@/components/common/statCardSurface";

/**
 * Order-board KPI tile. Unifies the compact chip (OrderKanban) and the large
 * clickable card (orders.tsx grid). `compact` = count over label; `large` =
 * label over count, rendered as a focusable button.
 */
export interface KpiStatProps {
  icon: ComponentType<{ className?: string }>;
  /** Classes for the icon box (background + icon colour). */
  iconClassName?: string;
  count: ReactNode;
  label: ReactNode;
  size?: "compact" | "large";
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function KpiStat({
  icon: Icon,
  iconClassName,
  count,
  label,
  size = "compact",
  onClick,
  className,
  "data-testid": testId,
}: KpiStatProps) {
  const iconBox = (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center",
        size === "large"
          ? "h-9 w-9 rounded-[0.6rem]"
          : "h-7.5 w-7.5 rounded-[0.55rem]",
        iconClassName,
      )}
    >
      <Icon className="h-4 w-4" />
    </div>
  );

  if (size === "large") {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={cn(
          statCardSurface,
          "flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left transition-[border-color] hover:border-(--color-text-primary)/30",
          className,
        )}
      >
        {iconBox}
        <div>
          <div className="mb-1 text-xs font-semibold leading-none text-(--color-text-secondary)">
            {label}
          </div>
          <div className="text-[1.625rem] font-extrabold leading-none tracking-tight text-(--color-text-primary)">
            {count}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div
      data-testid={testId}
      className={cn(
        statCardSurface,
        "flex shrink-0 items-center gap-2.5 px-3 py-2",
        className,
      )}
    >
      {iconBox}
      <div>
        <div className="text-[1.125rem] font-extrabold leading-none text-(--color-text-primary)">
          {count}
        </div>
        <div className="mt-0.5 text-[0.6875rem] font-semibold text-(--color-text-secondary)">
          {label}
        </div>
      </div>
    </div>
  );
}
